import React, { useEffect, useMemo, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Checkbox,
  Popover,
  ListItem,
  List,
  ListItemText,
  Drawer,
  Typography,
  IconButton,
  Tooltip,
  Box,
  Chip,
  Badge,
} from "@mui/material";
import { TableCellHead } from "../../table/tableCell";
import { STATUS } from "../../../utils/status";
import Type1AllocationTableRow from "./type1AllocationTableRow";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../redux/store/appStore";
import { useStyles } from "../../../assets/styles/listTableStyle";
import Error from "../../error/error";
import { getCompanyTaskList, getUpload } from "../../../redux/slices/upload";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import Pagination from "../../uploadTask/taskPagination";
import PaginationEntries from "../../pagination/paginationEntries";
import { clearIds, setIds } from "../../../redux/reducers/taskIdSlice";
import debounce from "lodash.debounce";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import { getVendorAction } from "../../../redux/slices/vendor";
import DrawerFilters from "../../Filters In Drawer/DrawerFilters";
import { drawerFilter } from "../../../utils/types";
import { AddButton } from "../../button/button";
import { getRoles } from "../../../redux/slices/projectSlice/addProjectSlice";
import { resetFilters, setFilters } from "../../../redux/reducers/filterReducer";
import SnackbarNotification from "../../snackbar/snackbarNotification";

interface Type1AllocationProps {
  allocationType: string | null;
  selectedStage: string | null;
  setSelectedStage: (stage: string | null) => void;
  selectedType: string;
}
interface Filters {
  taskName: string;
  pseudoname: string;
  expertId: string;
  selectedVendor: string;
  selectedRole?: string;
  selectedStage?: string;
  status?: string[];
  gigSourceId?: string;
}

const TASKING_STATUS_OPTIONS = [
  { label: 'Active', value: 'ACTIVE' },
  { label: 'Not Started', value: 'NOT STARTED' },
  { label: 'Quality Reject', value: 'QUALITY REJECT' },
  { label: 'Awaiting Review', value: 'AWAITING REVIEW' },
  { label: 'Quality Accepted', value: 'QUALITY ACCEPTED' },
  { label: 'Completed', value: 'COMPLETED' },
  { label: 'Delivered', value: 'DELIVERED' },
  {label : 'Draft', value: 'DRAFT'},
];

const REVIEWING_STATUS_OPTIONS = [
  { label: 'Active', value: 'ACTIVE' },
  { label: 'QC Pushback', value: 'QC PUSHBACK' },
  { label: 'Awaiting Task Update', value: 'AWAITING TASK UPDATE' },
  { label: 'Available to Review', value: 'AVAILABLE TO REVIEW' },
  { label: 'Quality Accepted', value: 'QUALITY ACCEPTED' },
  { label: 'Delivered', value: 'DELIVERED' },
  {label: "Quality Reject", value: "QUALITY REJECT" },
  { label: "Awaiting Review", value: "AWAITING REVIEW" },
  {label: 'Draft', value: 'DRAFT'},
];

const Type1Allocation: React.FC<Type1AllocationProps> = ({ selectedStage, setSelectedStage, selectedType }) => {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const dispatch = useDispatch<AppDispatch>();
  const [searchTerm] = useState("");
  const [loading] = useState(false);
  const [currentPage, setCurrentPage]: any = useState(1);
  const [itemsPerPage, setItemsPerPage]: any = useState<number>(10);
  const { id } = useParams();
  const { option } = useParams();
  const words = option && option.split(" ");
  const allocationtype = words && words[1]?.toLowerCase();
  const pathUnallocation = window.location.pathname.includes("Unallocation");

  const association_type = useSelector((state: RootState) => state.projects.association_type);

  const getStatus = useSelector((state: RootState) => state.upload.getStatus);
  const upload = useSelector((state: RootState) => state.upload.upload);
  const totalPages = Math.ceil((upload.count || 0) / itemsPerPage);
  const selectedIds = useSelector((state: RootState) => state.tasks.selectedIds);
  const currentPageTasks = upload.results || [];
  const currentSelectedIds = selectedIds.filter((selectedId) => currentPageTasks.some((task) => task.id === selectedId.id));
  const getReviewStages = useSelector((state: RootState) => state.reviewStage.reviewer);

  const [filterCountApplied, setFilterCountApplied]: any = useState(0);
  const [anchorE3, setAnchorE3] = useState(null);
  const [selectedValue] = useState("");
  const [taskName, setTaskName] = useState("");
  const [pseudoname] = useState("");
  const [expertId, setExpertId] = useState("");
  const [filterCount, setFilterCount]: any = useState(0);
  const [appliedFilterValues, setAppliedFilterValues] = useState<string[]>([]);
  const [vendorId, setVendorId] = useState("");
  const [options, setOptions] = useState<any[]>([]);
  const [openAutoComplete, setOpenAutoComplete] = React.useState(false);
  const [selectedVendor, setSelectedVendor] = useState<any>(null);
  const [selectedStatus] = useState<string[]>([]);
  const [searchTermAutocomplete, setSearchTermAutocomplete] = useState("");
  const [selectedNewTaskType, setSelectedNewTaskType] = useState<string[]>([]);
  const [filterTypes, setFilterType] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const appliedValues = useSelector((state: RootState) => state.filters);
  const [vendorPseudoName, setVendorPseudoName] = useState('');
  const [openDrawerFilter, setOpenDrawerFilter] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error" | "info" | "warning">("success");
  const stageRoles = useSelector((state: RootState) => state.projects.roles.results);
  const [selectedRole, setSelectedRole] = useState<string | null>(null);
  const [roleId, setRoleId] = useState('');
  const [status, setStatus] = useState<string[]>([]);
  const [gigSourceId, setGigSourceId] = useState('');

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const taskNameParam = searchParams.get("taskName") || "";
    const vendorParam = searchParams.get("vendorPseudoName") || "";
    const expertIdParam = searchParams.get("expertId") || "";
    const stageParam = searchParams.get("stage") || "";
    const roleParam = searchParams.get("role") || "";
    const statusParam = searchParams.get("status");
    const gigSourceIdParam = searchParams.get("gigSourceId") || "";
    const statusArray = statusParam ? JSON.parse(statusParam) : [];

    // Set local state from URL
    setTaskName(taskNameParam);
    setVendorPseudoName(vendorParam);
    setExpertId(expertIdParam);
    setSelectedStage(stageParam || null);
    setSelectedRole(roleParam || null);
    setStatus(statusArray);
    setGigSourceId(gigSourceIdParam);

    // Count applied filters
    const count = [
      taskNameParam,
      vendorParam,
      expertIdParam,
      stageParam,
      roleParam,
      gigSourceIdParam,
      statusArray.length > 0 ? "status" : ""
    ].filter(Boolean).length;

    setFilterCountApplied(count);
  }, []); // ✅ EMPTY DEPENDENCY - Run only once on mount

  const filterOptions = (options: { label: string; value: string }[], association_type: string) => {
    if (association_type === 'VENDOR') {
      return options.filter(
        (opt) => opt.value !== 'QUALITY ACCEPTED' && opt.value !== 'DELIVERED'
      );
    }
    return options;
  };

  const [statusOptions, setStatusOptions] = useState(filterOptions(TASKING_STATUS_OPTIONS, association_type));

  const headers = [
    "TASK NAME",
    `TASK STATUS`,
    `ROLE`,
    `VENDOR PSEUDO NAME`,
    `EXPERT ALLOCATION`,
  ];

  const types: string[] = [pathUnallocation ? 'for_unallocation' : 'for_allocation'];

  if (taskName) {
    types.push("task_name");
  }

  if (expertId) {
    types.push("expert_id");
  }

  if (vendorId) {
    types.push("GET_VENDOR_STATUS");
  }

  const allSelected =
    currentPageTasks.length > 0 &&
    currentPageTasks.every((row) => selectedIds.some((id) => id.id === row.id));
  const Vendors = useSelector((state: RootState) => state.vendors.vendors);
  const getVendorStatus = useSelector(
    (state: RootState) => state.vendors.getVendorsStatus
  );

  useEffect(() => {
    const stage = getReviewStages?.results?.find(
      (opt: any) => opt.id === selectedStage
    );

    if (stage?.category === 'TASK') {
      setStatusOptions(filterOptions(TASKING_STATUS_OPTIONS, association_type));
      if (allocationtype !== "allocation") {
        // setStatus([]);
      }
    }
    else if (stage?.category === 'REVIEW') {
      setStatusOptions(filterOptions(REVIEWING_STATUS_OPTIONS, association_type));
      // setStatus([]);
    }
  }, [selectedStage, getReviewStages]);

  // useEffect(() => {
  //   const payload = {
  //     search: searchTerm.trim(),
  //     project_id: id || "",
  //     page: currentPage,
  //     pageSize: itemsPerPage,
  //     paginate: true,
  //     vendor_id: vendorId,
  //     stage_id: selectedStage,
  //     ...(pathUnallocation && expertId !== '' && { expert_id: expertId }),
  //     ...(types.length > 0 && { type: types.join(",") }),
  //     role_id: selectedRole || '',
  //     ...(pathUnallocation && status.length > 0 && { status: status.join(',') }),
  //   };

  //   dispatch(setFilters({
  //     taskName: taskName || '',
  //     vendorPseudoName: vendorPseudoName || '',
  //     expertId: expertId || '',
  //     stage: selectedStage ? (getReviewStages?.results?.find((s: { id: string; }) => s.id === selectedStage)?.name || '') : '',
  //     role: selectedRole ? (stageRoles?.results.find((r: { id: string; }) => r.id === selectedRole)?.name || '') : '',
  //     status: status.map(s => statusOptions.find(opt => opt.value === s)?.label || s).join(', '),
  //   }));

  //   dispatch(getUpload(payload));
  // }, [
  //   dispatch,
  //   currentPage,
  //   itemsPerPage
  // ]);

  useEffect(() => {
    // Don't fetch if no filters applied
    if (filterCountApplied === 0) return;

    const types: string[] = [pathUnallocation ? 'for_unallocation' : 'for_allocation'];
    if (taskName) types.push("task_name");
    if (expertId) types.push("expert_id");
    if (vendorId) types.push("GET_VENDOR_STATUS");

    const payload = {
      // search: local_search_term.trim(),
      page: currentPage,
      pageSize: itemsPerPage,
      project_id: id || "",
      task_name: taskName.trim(),
      stage_id: selectedStage,
      ...(pathUnallocation && status.length > 0 ? { task_allocation_status: status.join(',') } : {}),
      task_allcation_type: "TASK,REVIEW",
      vendor_id: vendorId,
      ...(pathUnallocation && expertId !== '' && { expert: expertId }),
      ...(pathUnallocation && gigSourceId !== '' && { gig_source_id: gigSourceId }),
      is_allocated: true, 
      // auto_qa_passed:false,
      // role_id: selectedRole || '',
      // paginate: true,
      // ...(types.length > 0 ? { type: types.join(",") } : {}),
    };

    dispatch(getCompanyTaskList(payload));

    // Update Redux for display purposes only
    dispatch(setFilters({
      taskName: taskName || '',
      vendorPseudoName: vendorPseudoName || '',
      expertId: expertId || '',
      stage: selectedStage ? (getReviewStages?.results?.find((s: { id: string }) => s.id === selectedStage)?.name || '') : '',
      role: selectedRole ? (stageRoles?.results.find((r: { id: string }) => r.id === selectedRole)?.name || '') : '',
      status: status.map(s => statusOptions.find(opt => opt.value === s)?.label || s).join(', '),
    }));
  }, [currentPage,itemsPerPage,filterCountApplied]);

  useEffect(() => {
    dispatch(getVendorAction({ search_query: searchTerm.trim() }));
  }, [dispatch, searchTerm]);

  useEffect(() => {
    if (getVendorStatus === STATUS.SUCESS) {
      setOptions(Vendors.results);
    }
  }, [getVendorStatus]);

  const handleSelectAllClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClosePopover = () => {
    setAnchorEl(null);
  };

  const allIds = (upload.results || []).map((row) => ({
    id: row.id,
    task_name: row.task_name,
  }));

  const handleDoneClick = () => {
    const isAllSelectedOnPage = currentPageTasks.every((task) =>
      selectedIds.some((selectedId) => selectedId.id === task.id)
    );

    if (isAllSelectedOnPage) {
      const newSelectedIds = selectedIds.filter(
        (selectedId) =>
          !currentPageTasks.some((task) => task.id === selectedId.id)
      );
      dispatch(setIds(newSelectedIds));
    } else {
      const newSelectedIds = [
        ...selectedIds,
        ...currentPageTasks
          .filter(
            (task) =>
              !selectedIds.some((selectedId) => selectedId.id === task.id)
          )
          .map((task) => ({ id: task.id, task_name: task.task_name })),
      ];
      dispatch(setIds(newSelectedIds));
    }

    handleClosePopover();
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleSnackbar = (message: string, severity: "success" | "error" | "info" | "warning") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleChangeRowsPerPage = (value: number) => {
    setItemsPerPage(value as number);
    setCurrentPage(1);
    //handleApplyClick();
    //dispatch(getUpload({ search: searchTerm, page: 1, pageSize: value as number, project_id: id || "", type: type, for_allocation: true }));
  };

  const paginate = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const handleClick = (event: any) => {
    setOpenDrawerFilter(true);
  };

  useEffect(() => {
    const filters: Filters = {
      taskName,
      pseudoname,
      expertId,
      selectedVendor,
      selectedStage: selectedStage || '',
      selectedRole: selectedRole || '',
      status
    };
    const activeFilters: (keyof Filters)[] = Object.keys(filters).filter(
      (key) => {
        const value = filters[key as keyof Filters];
        if (Array.isArray(value)) return value.length > 0;
        return value !== "";
      }
    ) as (keyof Filters)[];

    const activeFilterValues = Object.values(filters).filter(
      (value) => {
        if (Array.isArray(value)) return value.length > 0;
        return value !== "";
      }
    );

    setFilterCount(activeFilters.length);
    setAppliedFilterValues(activeFilterValues);
  }, [
    taskName,
    pseudoname,
    expertId,
    selectedRole,
    selectedStage,
    selectedVendor,
    status,
  ]);

  const handleSearchChangeAutoComplete = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setSearchTermAutocomplete(event.target.value);
  };
  const debouncedResultsForAutoComplete = useMemo(() => {
    return debounce(handleSearchChangeAutoComplete, 300);
  }, []);
  useEffect(() => {
    return () => {
      debouncedResultsForAutoComplete.cancel();
    };
  });
  const handleAutocompleteInput = (e: any) => {
    debouncedResultsForAutoComplete(e);
  };

  const handleAutoCompleteOpen = () => {
    setOpenAutoComplete(true);
  };

  const handleAutoCompleteClose = () => {
    setOpenAutoComplete(false);
  };

  const handleApplyClick = async () => {
    // Validation checks before applying filters
    if (
      !taskName.trim() &&
      !expertId.trim() &&
      !vendorPseudoName?.trim() &&
      !selectedStage &&
      !gigSourceId.trim() &&
      status.length === 0
    ) {
      handleSnackbar("Please select at least one filter before applying.", "error");
      return;
    }

    if (taskName.trim() && !selectedStage) {
      handleSnackbar("Please select a stage when filtering by Task Name.", "error");
      return;
    }

    if (status.length > 0 && !selectedStage) {
      handleSnackbar("Please select a stage before choosing a status.", "error");
      return;
    }

    if (taskName.trim() && expertId.trim() && !selectedStage) {
      handleSnackbar("Please select a stage when filtering by Task Name.", "error");
      return;
    }

    let local_type = "";
    let local_search_term = searchTerm;

    let updatedSelectedNewTaskType = new Set<string>([...selectedNewTaskType]);
    let updatedSelectedStatus = new Set<string>([...selectedStatus]);

    if (taskName.trim().length > 0) {
      local_type = "task_name";
      updatedSelectedNewTaskType.add("task_name");
      local_search_term = taskName.trim();
    } else {
      updatedSelectedNewTaskType.delete("task_name");
    }

    if (expertId.trim().length > 0) {
      local_type = local_type ? `${local_type},expert_id` : "expert_id";
      updatedSelectedNewTaskType.add("expert_id");
    } else {
      updatedSelectedNewTaskType.delete("expert_id");
    }

    if (vendorPseudoName?.trim().length > 0) {
      local_type = local_type ? `${local_type},vendor_pseudo_name` : "vendor_pseudo_name";
      updatedSelectedNewTaskType.add("vendor_pseudo_name");
    } else {
      updatedSelectedNewTaskType.delete("vendor_pseudo_name");
    }

    if (selectedStage) {
      local_type = local_type ? `${local_type},stage` : "stage";
      updatedSelectedNewTaskType.add("stage");
    }

    if (status.length > 0) {
      status.forEach((statusItem) => updatedSelectedStatus.add(statusItem));
    }

    const updatedNewTaskTypeArray = Array.from(updatedSelectedNewTaskType);
    setSelectedNewTaskType(updatedNewTaskTypeArray);


    const params = new URLSearchParams();
    if (taskName.trim()) params.set("taskName", taskName.trim());
    if (vendorPseudoName?.trim()) params.set("vendorPseudoName", vendorPseudoName.trim());
    if (expertId.trim()) params.set("expertId", expertId.trim());
    if (gigSourceId.trim()) params.set("gigSourceId", gigSourceId.trim());
    if (selectedStage) params.set("stage", selectedStage);
    if (selectedRole) params.set("role", selectedRole);
    if (status.length > 0) params.set("status", JSON.stringify(status));

    navigate(`${location.pathname}?${params.toString()}`, { replace: true });

    dispatch(setFilters({
      taskName: taskName || '',
      vendorPseudoName: vendorPseudoName || '',
      expertId: expertId || '',
      gigSourceId: gigSourceId || '',
      stage: selectedStage ? (getReviewStages?.results?.find((s: { id: string; }) => s.id === selectedStage)?.name || '') : '',
      role: selectedRole ? (stageRoles?.results.find((r: { id: string; }) => r.id === selectedRole)?.name || '') : '',
      status: status.map(s => statusOptions.find(opt => opt.value === s)?.label || s).join(', '),
    }));

    setFilterCountApplied(
      Object.values(appliedValues).filter(
        (value) => value !== null && value !== undefined && value !== ''
      ).length
    );

    const payload = {
      // search: local_search_term.trim(),
      page: currentPage,
      pageSize: itemsPerPage,
      project_id: id || "",
      task_name: taskName.trim(),
      stage_id: selectedStage,
      ...(status && status.length > 0 && { task_allocation_status: status }),
      task_allcation_type: "TASK,REVIEW",
      vendor_id: vendorId,
      ...( expertId !== '' && { expert: expertId }),
      ...( gigSourceId !== '' && { gig_source_id: gigSourceId }),
      is_allocated: true, 
      // auto_qa_passed:false,
      // role_id: selectedRole || '',
      // paginate: true,
      // ...(types.length > 0 ? { type: types.join(",") } : {}),
    };

    dispatch(getCompanyTaskList(payload));

    setFilterType(local_type);
    setAnchorE3(null);
    dispatch(clearIds())
  };

  useEffect(() => {
    setFilterCountApplied(
      Object.values(appliedValues).filter(
        (value) => value !== null && value !== undefined && value !== ''
      ).length
    );
  }, [appliedValues]);

  useEffect(() => {
    if (selectedStage) {
      dispatch(getRoles({ project_id: id || '', stage_id: selectedStage || '' }))
    }
  }, [dispatch, id, selectedStage]);

  useEffect(() => {
    dispatch(
      getVendorAction({
        is_vendorblock: false,
        search_query: searchTermAutocomplete,
      })
    );
  }, [dispatch, searchTermAutocomplete]);

  const handleApply = () => {
    handleApplyClick();
    setOpenDrawerFilter(false);
  };

  const handleResetAll = () => {
    navigate(location.pathname, { replace: true });

    // Clear local state
    setTaskName('');
    setVendorPseudoName('');
    setExpertId('');
    setSelectedStage(null);
    setSelectedRole(null);
    setStatus([]);
    setVendorId('');
    setSelectedVendor(null);
    setRoleId('');
    setGigSourceId('');
    setFilterCountApplied(0);

    // Clear Redux
    dispatch(resetFilters());
    dispatch(clearIds());
  };

  const filters: drawerFilter[] = [
    {
      name: 'taskName',
      label: 'Task Name',
      type: 'text',
      value: taskName,
      onChange: setTaskName,
      reset: () => setTaskName(''),
    },
    ...(pathUnallocation
      ? [
        {
          name: 'expertId',
          label: 'Expert Email / ID',
          type: 'text',
          value: expertId,
          onChange: setExpertId,
          reset: () => setExpertId(''),
        } as drawerFilter,
        {
          name: 'gigSourceId',
          label: 'Gig Source Id',
          type: 'text',
          value: gigSourceId,
          onChange: setGigSourceId,
          reset: () => setGigSourceId(''),
        } as drawerFilter,
      ]
      : []),
    ...(association_type !== 'VENDOR'
      ? [
        {
          name: 'vendorPseudoName',
          label: 'Vendor Pseudoname',
          type: 'autocomplete' as const,
          value: selectedVendor,
          onChange: (vendor: { pseudo_name: string; vendor_id: string }) => {
            setVendorId(vendor?.vendor_id || '');
            setSelectedVendor(vendor || null);
            setVendorPseudoName(vendor?.pseudo_name);
          },
          reset: () => {
            setVendorId('');
            setSelectedVendor(null);
          },
          options,
          open: openAutoComplete,
          onOpen: handleAutoCompleteOpen,
          onClose: handleAutoCompleteClose,
          inputValue: selectedVendor != null ? selectedVendor.name : '',
          onInputChange: handleAutocompleteInput,
          getOptionLabel: (opt: any) =>
            opt?.pseudo_name || opt?.vendor_pseudo_name || '',
          loading,
        },
      ]
      : []),
    {
      name: 'stage',
      label: 'Stages',
      type: 'autocomplete',
      value: getReviewStages?.results?.find((opt: any) => opt.id === selectedStage) || null,
      onChange: (stage: { id: string; name: string } | null) => {
        setSelectedStage(stage?.id || null);
      },
      reset: () => {
        setSelectedStage(null);
      },
      options: getReviewStages?.results || [],
      getOptionLabel: (opt: any) => opt?.name || '',
    },
    {
      name: 'role',
      label: 'Roles',
      type: 'autocomplete',
      value: stageRoles?.results.find((r: { id: string }) => r.id === roleId) || null,
      onChange: (role) => {
        setSelectedRole(role?.id || null);
        setRoleId(role?.id || '');
      },
      reset: () => {
        setSelectedRole(null);
        setRoleId('');
      },
      options: stageRoles?.results || [],
      getOptionLabel: (opt) => opt?.name || '',
    },
    ...(pathUnallocation
      ? [
        {
          name: 'status',
          label: 'Status',
          type: 'multiselect',
          value: status,
          onChange: (newVal: string[]) => setStatus(newVal || []),
          reset: () => setStatus([]),
          options: statusOptions,
          getOptionLabel: (opt) => opt?.label || '',
          disabled: !selectedStage,
        } as drawerFilter,
      ]
      : []),
  ];

  const handleDeleteChip = (key: keyof typeof appliedValues) => {
    const params = new URLSearchParams(searchParams);
    if (key === 'taskName') setTaskName('');
    if (key === 'expertId') setExpertId('');
    if (key === 'gigSourceId') setGigSourceId('');
    if (key === 'vendorPseudoName') {
      setVendorId('');
      setVendorPseudoName('');
      setSelectedVendor(null);
    }
    if (key === 'role') {
      setSelectedRole(null);
      setRoleId('');
    }
    if (key === 'status') {
      setStatus([]);
    }

    dispatch(setFilters({
      taskName: key === 'taskName' ? '' : taskName || '',
      vendorPseudoName: vendorPseudoName || '',
      expertId: key === 'expertId' ? '' : expertId || '',
      gigSourceId: key === 'gigSourceId' ? '' : gigSourceId || '',
      stage: selectedStage ? (getReviewStages?.results?.find((s: { id: string; }) => s.id === selectedStage)?.name || '') : '',
      role: selectedRole ? (stageRoles?.results.find((r: { id: string; }) => r.id === selectedRole)?.name || '') : '',
      status: key === 'status' ? '' : status.map(s => statusOptions.find(opt => opt.value === s)?.label || s).join(', '),
    }));

    const payload = {
      search: key === 'taskName' ? '' : taskName.trim(),
      project_id: id || '',
      page: currentPage,
      pageSize: itemsPerPage,
      paginate: true,
      vendor_id: key === 'vendorPseudoName' ? '' : vendorId,
      stage_id: key === 'stage' ? null : selectedStage,
      expert_id: key === 'expertId' ? '' : expertId || '',
      ...(types.length > 0 && { type: types.join(",") }),
      role_id: key === 'role' ? '' : selectedRole || '',
      ...(pathUnallocation && key !== 'status' && status.length > 0 && { status: status.join(',') }),
    };
    navigate(`${location.pathname}?${params.toString()}`, { replace: true })
    setFilterCountApplied((prev: any) => Math.max(0, prev - 1));
    // dispatch(getUpload(payload));
  };

  useEffect(() => {
    setTaskName(appliedValues.taskName);
    setVendorPseudoName(appliedValues.vendorPseudoName);
    setExpertId(appliedValues.expertId || '');
    setGigSourceId(appliedValues.gigSourceId || '');
    setSelectedStage(
      getReviewStages?.results?.find((s: { name: string; }) => s.name === appliedValues.stage)?.id || null
    );
    setSelectedRole(
      stageRoles?.results.find((r: { name: string; }) => r.name === appliedValues.role)?.id || null
    );

    if (appliedValues.status && typeof appliedValues.status === 'string') {
      const statusLabels = appliedValues.status.split(', ');
      const statusValues = statusLabels.map(label =>
        statusOptions.find(opt => opt.label === label)?.value || label
      ).filter(Boolean);
      setStatus(statusValues);
    } else {
      setStatus([]);
    }
  }, [appliedValues]);

  const activeChips = Object.entries(appliedValues)
    .filter(([key, value]) => {
      if (Array.isArray(value)) return value.length > 0;
      return value !== null && value !== undefined && value !== '';
    }).map(([key, value]) => {
      let label = '';
      switch (key) {
        case 'taskName':
          label = `Task Name: ${value}`;
          break;
        case 'expertId':
          label = `Expert ID: ${value}`;
          break;
        case 'gigSourceId':
          label = `Gig Source Id: ${value}`;
          break;
        case 'vendorPseudoName':
          label = `Vendor: ${value}`;
          break;
        case 'stage':
          label = `Stage: ${value}`;
          break;
        case 'role':
          label = `Role: ${value}`;
          break;
        case 'status':
          label = `Status: ${value}`;
          break;
        default:
          return null;
      }

      return (
        <Chip
          key={key}
          label={label}
          {...(key !== 'stage' && {
            onDelete: () => handleDeleteChip(key as keyof typeof appliedValues),
          })}
          sx={{ mr: 1, mb: 1 }}
        />
      );
    }).filter(Boolean);

  // React.useEffect(() => {
  //   const params: any = {};
  //   if (appliedValues.taskName) params.taskName = appliedValues.taskName;
  //   if (appliedValues.expertId) params.expertId = appliedValues.expertId;
  //   if (appliedValues.vendorPseudoName) params.vendorPseudoName = appliedValues.vendorPseudoName;
  //   if (appliedValues.stage) params.stage = appliedValues.stage;
  //   if (appliedValues.role) params.role = appliedValues.role;
  //   if (appliedValues.status) params.status = appliedValues.status;

  //   setSearchParams(params);
  // }, [appliedValues, setSearchParams]);

  return (
    <>
      <div
        style={{
          display: "flex",
          height: "6.5vh",
          justifyContent: "space-between",
          marginBottom: "5px"
        }}
      >
        {filterCountApplied > 0 &&
          <div style={{ display: "flex" }}>
            <Typography
              style={{
                fontFamily: "Montserrat",
                fontSize: "18px",
                fontWeight: "600",
                marginTop: "10px",
              }}
            >
              {allocationtype === "allocation" ? `Unallocated Tasks` : `Allocated Tasks 
                 ${!!selectedStatus && selectedStatus.length > 0 ? selectedStatus.length <= 3 ? selectedStatus.join(", ") : [...selectedStatus.slice(0, 3), `+${selectedStatus.length - 3}`,].join(", ") : allocationtype === "allocation" ? ["Not Allocated"] : ""}`
              }
            </Typography>
            <Typography
              style={{
                fontFamily: "Montserrat",
                fontSize: "18px",
                fontWeight: "400",
                marginTop: "10px",
                marginLeft: "10px",
                color: "grey",
                marginRight: "10px",
              }}
            >
              (
              {getStatus === STATUS.PENDING ? (
                <CircularProgress size={"20px"} />
              ) : (
                `${selectedIds.length > 0 && getStatus === STATUS.SUCESS
                  ? `${selectedIds.length}/${upload.count}`
                  : `${upload.count}`
                }`
              )}
              )
            </Typography>
          </div>
        }

        <div style={{ flex: 3, marginTop: 20 }} className={classes.actionsContainer}>
          {filterCountApplied > 0 &&
            <PaginationEntries
              itemsPerPage={itemsPerPage}
              onChangeRowsPerPage={handleChangeRowsPerPage}
            />
          }
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              marginRight: "20px",
            }}
          >
            <IconButton
              color="primary"
              aria-label="filter options"
              onClick={handleClick}
              value={selectedValue}
              autoFocus={false}
              sx={{
                boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
                background: "linear-gradient(45deg, #42a5f5, #478ed1)",
                borderRadius: "30%",
                transition: "transform 0.3s ease-in-out",
                "&:hover": {
                  background: "linear-gradient(45deg, #478ed1, #42a5f5)",
                  transform: "scale(1.1)",
                },
              }}
            >
              <Tooltip title="Filters" placement="bottom-end" arrow>
                <Badge
                  badgeContent={filterCountApplied}
                  color="error"
                  invisible={filterCountApplied === 0}
                >
                  <FilterAltIcon fontSize="medium" sx={{ color: "white" }} />
                </Badge>
              </Tooltip>
            </IconButton>
          </Box>
        </div>
      </div>
      <Box sx={{ pb: 1, display: 'flex', flexWrap: 'wrap' }}>
        {activeChips.length > 0 ? activeChips : ""}
      </Box>
      <div style={{ overflowY: "scroll", height: "52.5vh" }}>
        <TableContainer>
          {loading && (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                marginTop: "20px",
              }}
            >
              <CircularProgress />
            </div>
          )}
          {!loading && (
            <>
              {filterCountApplied === 0 &&
                <Box
                  sx={{
                    height: '300px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    flexDirection: 'column',
                    border: '1px dashed #ccc',
                    borderRadius: 2,
                    backgroundColor: '#f9f9f9',
                    p: 3,
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{ mb: 2, color: 'text.secondary', textAlign: 'center' }}
                  >
                    📢 Please apply filters to get data in the table
                  </Typography>
                  <AddButton onClick={handleClick} children="Filters" startIcon={<FilterAltIcon />} />
                </Box>
              }
              {filterCountApplied > 0 &&
                <Table sx={{ minWidth: 650 }} aria-label="simple table">
                  <TableHead
                    sx={{
                      overflow: "hidden",
                      backgroundColor: "#F7F9FA",
                      "&:first-child td, &:first-child th": { borderBottom: 0 },
                    }}
                  >
                    <TableRow>
                      <TableCellHead>
                        <Checkbox
                          checked={allSelected}
                          onClick={handleSelectAllClick}
                          size="small"
                        />
                        <Popover
                          open={Boolean(anchorEl)}
                          anchorEl={anchorEl}
                          onClose={handleClosePopover}
                          anchorOrigin={{
                            vertical: "bottom",
                            horizontal: "center",
                          }}
                          transformOrigin={{
                            vertical: "top",
                            horizontal: "left",
                          }}
                        >
                          <div>
                            <List>
                              <ListItem
                                selected={allIds.length === selectedIds.length}
                                onClick={() => handleDoneClick()}
                                sx={{ cursor: "pointer" }}
                              >
                                <ListItemText
                                  primary={`${currentSelectedIds.length ===
                                    currentPageTasks.length
                                    ? "Deselect"
                                    : "Select"
                                    } ${currentPageTasks.length} tasks`}
                                />
                              </ListItem>
                            </List>
                          </div>
                        </Popover>
                      </TableCellHead>
                      {headers.map((header) => (
                        <TableCellHead key={header}>{header}</TableCellHead>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {getStatus === STATUS.SUCESS &&
                      upload &&
                      upload.results &&
                      upload.results.length > 0 ? (
                      upload.results.map((row: any) => (
                        <>
                          <Type1AllocationTableRow key={row.id} row={row} />
                        </>
                      ))
                    ) : (
                      getStatus === STATUS.PENDING ? (
                        <CircularProgress size={"30px"} style={{ margin: "auto" }} />
                      ) : (
                        <TableRow>
                          <TableCell
                            colSpan={headers.length}
                            className={classes.noUsersMessage}
                          >
                            <Error
                              message={
                                "There are no tasks added as of yet. You can add a task to see it here."
                              }
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              }
            </>
          )}
        </TableContainer>
      </div>


      <Drawer
        anchor={"right"}
        open={openDrawerFilter}
        onClose={() => setOpenDrawerFilter(false)}
        PaperProps={{ sx: { width: "24%", padding: "40px" } }}
      >
        <DrawerFilters
          filters={filters}
          onApply={handleApply}
          onResetAll={handleResetAll}
          setOpenDrawerFilter={setOpenDrawerFilter}
          stages={getReviewStages?.results || []}
          disabled={!selectedStage}
        />
      </Drawer>

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        paginate={paginate}
        itemsPerPage={itemsPerPage}
      />
      <SnackbarNotification
        open={snackbarOpen}
        message={snackbarMessage}
        severity={snackbarSeverity}
        onClose={handleSnackbarClose}
      />
    </>
  );
};

export default Type1Allocation;